import { useCallback } from 'react';

import { useTypedSearchParams } from 'react-router-typesafe-routes';
import { useSearchParams } from 'react-router-dom';

import { ROUTES } from '../../../../routes';

export type DialogUrlParams = {
  messageChannel: string | null;
  text: string | null;
  to: string | null;
};

export type UseDialogUrlParamsResult = {
  params: DialogUrlParams;
  clearParams: () => void;
};

const PARAM_KEYS = {
  MESSAGE_CHANNEL: 'message_channel',
  TEXT: 'text',
  TO: 'to',
} as const;

/**
 * Hook to read and manage dialog-specific URL parameters.
 * Supports:
 *   - message_channel=sms - Pre-select SMS compose mode
 *   - text=some_text - Pre-populate message text
 *   - to=+12345678912 - Pre-select recipient phone number
 */
export const useDialogUrlParams = (): UseDialogUrlParamsResult => {
  const [typedParams] = useTypedSearchParams(ROUTES.customer);
  const [, setSearchParams] = useSearchParams();

  const params: DialogUrlParams = {
    messageChannel: typedParams.message_channel ?? null,
    text: typedParams.text ?? null,
    to: typedParams.to ?? null,
  };

  const clearParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(PARAM_KEYS.MESSAGE_CHANNEL);
        newParams.delete(PARAM_KEYS.TEXT);
        newParams.delete(PARAM_KEYS.TO);
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { params, clearParams };
};
